import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';  // Use BrowserRouter
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import './App.css';
import Seo from './components/Seo';
import Navbar from './components/Navbar';
import Home from './components/Home';
import AboutUs from './components/AboutUs';
import Contact from './components/Contact';

// Home stays eager (it is the landing page); other routes load on demand.
const Pricing = lazy(() => import('./components/Pricing'));
const PartnerWithUs = lazy(() => import('./components/PartnerWithUs'));
const PrivacyPolicyUser = lazy(() => import('./components/terms/PrivacyPolicyUser'));
const PrivacyPolicyPartner = lazy(() => import('./components/terms/PrivacyPolicyPartner'));
const TermsUser = lazy(() => import('./components/terms/TermsUser'));
const TermsPartner = lazy(() => import('./components/terms/TermsPartner'));
const PartnerDeleteAccountPage = lazy(() => import('./components/terms/PartnerDeleteAccountPage'));
const UserDeleteAccountPage = lazy(() => import('./components/terms/UserDeleteAccountPage'));

const theme = createTheme({
  palette: {
    primary: { main: '#2085D8', dark: '#135F97', light: '#5DAEEA' },
    secondary: { main: '#0EA5E9' },
    background: { default: '#ffffff' },
    text: { primary: '#111827', secondary: '#4b5563' },
  },
  typography: {
    fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h1: { fontWeight: 800, letterSpacing: '-0.03em' },
    h2: { fontWeight: 800, letterSpacing: '-0.02em' },
    h3: { fontWeight: 800, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 999, paddingInline: '1.35rem' },
        containedPrimary: {
          boxShadow: '0 10px 24px rgba(32, 133, 216, 0.28)',
          '&:hover': { boxShadow: '0 14px 30px rgba(32, 133, 216, 0.36)' },
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <div className="content">
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<>
              <Seo
                path="/"
                title="Cizlot | Salon & Parlour Appointment Booking App in India"
                description="Book salon and parlour appointments online with Cizlot. Find salons near you, reserve slots instantly and skip the wait. Salon owners get a full booking dashboard."
                keywords="salon booking app, parlour booking, salon appointment, online salon booking india, book salon near me, beauty parlour appointment"
              />
              <Home />
            </>} />
            <Route path="/about" element={<>
              <Seo
                path="/about"
                title="About Cizlot | Online Salon & Parlour Booking Platform"
                description="Cizlot connects customers with salons and parlours across India for real-time slot booking, and gives salon owners simple dashboards for staff, services and schedules."
                keywords="about cizlot, salon booking platform india, parlour appointment app, salon management software"
              />
              <AboutUs />
            </>} />
            <Route path="/contact" element={<>
              <Seo
                path="/contact"
                title="Contact Cizlot | Salon Booking App Support"
                description="Get in touch with the Cizlot team for support with salon and parlour bookings, partner onboarding, or your salon dashboard. We usually reply within one business day."
                keywords="cizlot contact, salon booking support, parlour app help, salon software support india"
              />
              <Contact />
            </>} />
            <Route path="/pricing" element={<>
              <Seo
                path="/pricing"
                title="Pricing Plans | Salon & Parlour Booking Software – Cizlot"
                description="Compare Cizlot pricing plans for salons and parlours: free plan, unlimited slots, staff logins, multi-branch support, analytics and revenue reports. Start free."
                keywords="salon software pricing, parlour booking app price, salon management plans, salon appointment software cost india"
              />
              <Pricing />
            </>} />
            <Route path="/partner" element={<>
              <Seo
                path="/partner"
                title="Partner With Cizlot | Salon Owner Booking Dashboard"
                description="List your salon or parlour on Cizlot. Manage appointments, staff, slots and revenue from one dashboard, and get discovered by customers booking online near you."
                keywords="partner with cizlot, list my salon online, salon owner dashboard, parlour booking management, salon appointments dashboard"
              />
              <PartnerWithUs />
            </>} />
            <Route path="/navbar" element={<>
              <Seo path="/navbar" title="Cizlot" description="Cizlot" noindex />
              <Navbar />
            </>} />

            <Route path="/legal/user/privacy-policy" element={<>
              <Seo
                path="/legal/user/privacy-policy"
                title="Privacy Policy for Users | Cizlot"
                description="How Cizlot collects, uses and protects personal data of customers booking salon and parlour appointments."
              />
              <PrivacyPolicyUser />
            </>} />
            <Route path="/legal/user/terms-of-service" element={<>
              <Seo
                path="/legal/user/terms-of-service"
                title="Terms of Service for Users | Cizlot"
                description="Terms and conditions for customers using Cizlot to book salon and parlour appointments online."
              />
              <TermsUser />
            </>} />
            <Route path="/legal/partner/privacy-policy" element={<>
              <Seo
                path="/legal/partner/privacy-policy"
                title="Privacy Policy for Salon Partners | Cizlot"
                description="How Cizlot handles data of salon and parlour partners using the booking and management dashboard."
              />
              <PrivacyPolicyPartner />
            </>} />
            <Route path="/legal/partner/terms-of-service" element={<>
              <Seo
                path="/legal/partner/terms-of-service"
                title="Terms of Service for Salon Partners | Cizlot"
                description="Terms and conditions for salons and parlours partnering with Cizlot for online bookings and management."
              />
              <TermsPartner />
            </>} />

            <Route path="/legal/partner/delete-account" element={<>
              <Seo
                path="/legal/partner/delete-account"
                title="Delete Your Cizlot Partner Account"
                description="Steps for salon and parlour partners to request deletion of their Cizlot account and business data."
              />
              <PartnerDeleteAccountPage />
            </>} />
            <Route path="/legal/user/delete-account" element={<>
              <Seo
                path="/legal/user/delete-account"
                title="Delete Your Cizlot Account | Users"
                description="Steps to request deletion of your Cizlot customer account and associated salon booking data."
              />
              <UserDeleteAccountPage />
            </>} />

          </Routes>
          </Suspense>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
